import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Papel } from '@du/shared'
import { api, aoDeslogar, guardarAcesso, lerAcesso, renovar } from './api.js'

export type Usuario = {
  id: string
  nome: string
  email: string
  papel: Papel
  trocarSenha: boolean
}

type Sessao = {
  usuario: Usuario | null
  carregando: boolean
  entrar: (email: string, senha: string) => Promise<void>
  sair: () => Promise<void>
}

const Ctx = createContext<Sessao | null>(null)

export function useSessao() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useSessao precisa estar dentro de <ProvedorSessao>')
  return ctx
}

export function ProvedorSessao({ children }: { children: React.ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [carregando, setCarregando] = useState(true)

  /**
   * Na abertura, tenta restaurar a sessão pelo cookie de refresh. É isto que
   * faz o tablet da cozinha, ligado o dia todo, não pedir login a cada F5.
   */
  useEffect(() => {
    let vivo = true
    void (async () => {
      try {
        // renovar() do api.ts, e NÃO um fetch próprio: ele garante uma
        // renovação por vez. Chamar direto fazia o StrictMode (que monta o
        // efeito duas vezes em desenvolvimento) disparar dois refresh
        // simultâneos — o segundo recebia 409 RENOVACAO_CONCORRENTE e a
        // sessão caía. Duas abas abrindo juntas em produção fariam igual.
        if (!lerAcesso() && !(await renovar())) return
        const eu = await api<Usuario>('/v1/auth/me')
        if (vivo) setUsuario(eu)
      } catch {
        // Sem cookie válido: cai na tela de login, que é o esperado.
      } finally {
        if (vivo) setCarregando(false)
      }
    })()
    return () => {
      vivo = false
    }
  }, [])

  useEffect(() => aoDeslogar(() => setUsuario(null)), [])

  const entrar = useCallback(async (email: string, senha: string) => {
    const r = await api<{ access: string; usuario: Usuario }>('/v1/auth/login', {
      method: 'POST',
      corpo: { email, senha },
      semAuth: true,
    })
    guardarAcesso(r.access)
    setUsuario(r.usuario)
  }, [])

  const sair = useCallback(async () => {
    try {
      await api('/v1/auth/logout', { method: 'POST' })
    } finally {
      guardarAcesso(null)
      setUsuario(null)
    }
  }, [])

  const valor = useMemo(
    () => ({ usuario, carregando, entrar, sair }),
    [usuario, carregando, entrar, sair],
  )
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>
}
